import { useEffect } from "react";
import apiClient from "../../api/axios";
import { JobStatus, FileWithStatus } from "./types";
import { ExtractedContract, RentRollItem } from "../../types/contract";

export const useJobPolling = (
  flowState: string,
  jobStatuses: Record<string, JobStatus>,
  setJobStatuses: (updater: (prev: Record<string, JobStatus>) => Record<string, JobStatus>) => void,
  generalContracts: ExtractedContract[],
  setGeneralContracts: (contracts: ExtractedContract[]) => void,
  rentRollData: RentRollItem[],
  setRentRollData: (data: RentRollItem[]) => void,
  fileLists: { valid: FileWithStatus[]; invalid: FileWithStatus[] },
  failedUploads: string[],
  setSnackbar: (snackbar: { open: boolean; message: string; severity?: "success" | "info" | "warning" | "error" }) => void,
  setAllFailedFilesForSummary: (files: string[]) => void,
  setTotalFilesForProgressBar: (total: number) => void,
  setCompletedFilesForProgressBar: (completed: number) => void,
  setIsSummaryDataReady: (ready: boolean) => void,
  POLLING_INTERVAL_GENERAL_MS: number,
  POLLING_JITTER_MS: number
) => {
  useEffect(() => {
    if (flowState !== "processing" || Object.keys(jobStatuses).length === 0) {
      return;
    }

    let activeTimeouts: Record<string, NodeJS.Timeout> = {};

    const schedulePoll = async (jobId: string, initialDelay = 0) => {
      // Add a random jitter to the initial delay to distribute requests
      const jitter = Math.random() * POLLING_JITTER_MS;
      const delay = initialDelay + jitter;

      activeTimeouts[jobId] = setTimeout(async () => {
        try {
          const response = await apiClient.get(`/api/job-status/${jobId}`);
          if (response.data.status === "complete") {
            setJobStatuses((prev) => ({
              ...prev,
              [jobId]: {
                ...prev[jobId],
                status: "complete",
                data: response.data.data,
              },
            }));
            // Clear timeout for this job, it's complete
            if (activeTimeouts[jobId]) clearTimeout(activeTimeouts[jobId]);
            delete activeTimeouts[jobId];
          } else {
            // Still processing, schedule next poll for this job
            schedulePoll(jobId, POLLING_INTERVAL_GENERAL_MS);
          }
        } catch (err) {
          console.error(`Error polling job ${jobId}:`, err);
          setJobStatuses((prev) => ({
            ...prev,
            [jobId]: {
              ...prev[jobId],
              status: "error",
              error: "ポーリング中にエラーが発生しました。",
            },
          }));
          // Clear timeout for this job, it's errored
          if (activeTimeouts[jobId]) clearTimeout(activeTimeouts[jobId]);
          delete activeTimeouts[jobId];
        }
      }, delay);
    };

    // Check if all jobs are done globally
    const allJobsDone = Object.values(jobStatuses).every(
      (job) => job.status === "complete" || job.status === "error"
    );

    if (allJobsDone) {
      const processAndFetchIfNeeded = async () => {
        const newExtractedContracts: ExtractedContract[] = [];
        const newExtractedRentRolls: RentRollItem[] = [];
        const failedProcessingFiles: string[] = [];
        const existingContractFileIds = new Set(generalContracts.map(c => c.fileId));

        let newRentRollProcessed = false;

        Object.values(jobStatuses).forEach((job) => {
          if (job.status === "complete" && job.data) {
            if (job.data.type === "contract") {
              if (existingContractFileIds.has(job.fileId)) {
                return;
              }
              newExtractedContracts.push({
                ...job.data.data,
                fileId: job.fileId,
              });
            } else if (job.data.type === "rent_roll") {
              newRentRollProcessed = true;
              newExtractedRentRolls.push(...job.data.data);
            }
          } else if (job.status === "error") {
            failedProcessingFiles.push(job.file.name);
          }
        });

        const updatedGeneralContracts = [
          ...generalContracts,
          ...newExtractedContracts,
        ];

        const updatedRentRollData = newRentRollProcessed ? newExtractedRentRolls : rentRollData;

        const allFailedFiles = [
          ...new Set([...failedUploads, ...failedProcessingFiles]),
        ];
        if (allFailedFiles.length > 0) {
          setSnackbar({
            open: true,
            message: `${allFailedFiles.join(", ")} の処理に失敗しました。`,
          });
          setAllFailedFilesForSummary(allFailedFiles);
        }

        setGeneralContracts(updatedGeneralContracts);
        setRentRollData(updatedRentRollData);
        setTotalFilesForProgressBar(fileLists.valid.length);
        setCompletedFilesForProgressBar(Object.keys(jobStatuses).length);
        setIsSummaryDataReady(true);
      };
      processAndFetchIfNeeded();
      return () => {
        // Clear any lingering timeouts when all jobs are done or component unmounts
        Object.values(activeTimeouts).forEach(clearTimeout);
      };
    }

    // Update progress bar
    const completedOrErroredJobs = Object.values(jobStatuses).filter(
      (j) => j.status === "complete" || j.status === "error"
    );
    setCompletedFilesForProgressBar(completedOrErroredJobs.length);

    // Initial scheduling for all processing jobs that haven't been scheduled yet
    Object.keys(jobStatuses).forEach((jobId) => {
      if (jobStatuses[jobId].status === "processing" && !activeTimeouts[jobId]) {
        // Only for the very first poll, introduce a 10-second delay.
        // Subsequent polls will use POLLING_INTERVAL_GENERAL_MS.
        schedulePoll(jobId, 10000); // 10 seconds initial delay
      }
    });
    
    return () => {
      // Cleanup: Clear all timeouts if component unmounts or dependencies change
      Object.values(activeTimeouts).forEach(clearTimeout);
    };
  }, [
    flowState, 
    jobStatuses, 
    generalContracts, 
    rentRollData, 
    fileLists.valid.length, 
    failedUploads, 
    setJobStatuses,
    setGeneralContracts,
    setRentRollData,
    setSnackbar,
    setAllFailedFilesForSummary,
    setTotalFilesForProgressBar,
    setCompletedFilesForProgressBar,
    setIsSummaryDataReady,
    POLLING_INTERVAL_GENERAL_MS,
    POLLING_JITTER_MS
  ]);
};